<template>
  <div>
    <div class="relative" :class="{ 'opacity-40': disabled }">
      <input
        :id="namePrefix + name"
        ref="input"
        v-model="value"
        :type="revealed ? 'text' : type"
        :name="namePrefix + name"
        :placeholder="isOptional ? $t('formInput.optional') : placeholder"
        :disabled="disabled"
        :class="[
          apiErrorMessage || shouldDisplayErrors
            ? 'text-red-600 placeholder-red-300'
            : borderColor !== null
              ? 'placeholder-' + borderColor
              : 'placeholder-medium',
          prependText ? 'pl-' + prependTextPadding : '',
          prependIcon ? 'pl-10' : '',
          appendText
            ? apiErrorMessage || shouldDisplayErrors
              ? 'pr-' + (appendTextPadding + 8)
              : 'pr-' + appendTextPadding
            : '',
          invisible ? 'h-8 px-0' : 'h-12 rounded-lg px-4',
          inputBorderClass,
          backgroundColor,
          textAlignRight ? 'text-right' : 'text-left',
          optionalInputClass,
        ]"
        :autocomplete="autocomplete"
        :inputmode="inputMode"
        class="peer mx-px block w-full transition-[border-color] duration-200 ease-in focus:mx-0 focus:outline-0"
        @blur="handleBlur"
        @change="handleChange"
      />
      <div
        v-if="apiErrorMessage || shouldDisplayErrors"
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4"
      >
        <i class="far fa-exclamation-circle text-red-500"></i>
      </div>
      <div
        v-if="prependText || prependIcon"
        class="pointer-events-none absolute inset-y-0 left-0 items-center text-gray-700"
        :class="[
          (value === '' || value === null || value === undefined) &&
          placeholder === '' &&
          isOptional === false
            ? 'hidden peer-focus:flex'
            : 'flex',
          { 'pl-4': !invisible },
        ]"
      >
        <i v-if="prependIcon" :class="prependIconClass"></i>
        {{ prependText }}
      </div>
      <div
        v-if="type === 'password'"
        class="absolute inset-y-0 right-0 flex items-center"
        :class="[apiErrorMessage || shouldDisplayErrors ? 'pr-12' : 'pr-4']"
      >
        <i
          class="far cursor-pointer text-gray-700"
          :class="revealed ? 'fa-eye-slash' : 'fa-eye'"
          :data-cy="'reveal-' + namePrefix + name"
          @click="revealed = !revealed"
        ></i>
      </div>
      <div
        v-if="appendText || resetIcon"
        class="absolute inset-y-0 right-0 flex items-center text-gray-700"
        :class="[apiErrorMessage || shouldDisplayErrors ? 'pr-10' : 'pr-4']"
      >
        <i
          v-if="resetIcon && !apiErrorMessage && !shouldDisplayErrors"
          class="far fa-times-circle cursor-pointer text-gray-700"
          @click="value = ''"
        ></i>
        {{ appendText }}
      </div>
      <label
        v-if="label"
        :for="namePrefix + name"
        class="absolute block rounded-lg transition-all duration-200 ease-in"
        :class="[
          {
            'text-gray-500 peer-hover:text-gray-900 peer-focus:text-primary-800':
              !shouldDisplayErrors && !apiErrorMessage,
            'text-red-500': shouldDisplayErrors || apiErrorMessage,
          },
          backgroundColor,
          prependIcon
            ? (value === '' || value === null) &&
              placeholder === '' &&
              isOptional === false
              ? 'ml-4 peer-focus:ml-0'
              : 'ml-0'
            : '',
          (value === '' || value === null || value === undefined) &&
          placeholder === '' &&
          isOptional === false
            ? 'left-4 top-6 -translate-y-1/2 cursor-text text-base peer-focus:left-4 peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:px-1 peer-focus:text-sm'
            : 'left-4 top-0 -translate-y-1/2 rounded-lg px-1 text-sm font-medium',
        ]"
      >
        {{ label }}
      </label>
    </div>

    <div :data-cy="'error-' + namePrefix + name">
      <form-error-message v-if="apiErrorMessage">
        {{ apiErrorMessage }}
      </form-error-message>
      <div v-if="shouldDisplayErrors">
        <form-error-message v-for="veeError in veeErrors" :key="veeError">
          {{ veeError }}
        </form-error-message>
      </div>
    </div>

    <form-help-text
      v-if="helpText && !shouldDisplayErrors && !apiErrorMessage"
      >{{ helpText }}</form-help-text
    >
  </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed, onMounted, PropType, ref, toRef } from 'vue';
import { TranslateResult } from 'vue-i18n';
import { useFormApiError } from '@/composables/form-api-error';
import FormErrorMessage from '@/components/shared/forms/FormErrorMessage.vue';
import FormHelpText from '@/components/shared/forms/FormHelpText.vue';
import { useFormError } from '@/composables/form-error';
import { useFormHelper } from '@/composables/form-helper';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  apiErrors: {
    type: Object,
    default: () => ({}),
  },
  label: {
    type: String as PropType<TranslateResult | string>,
    required: false,
    default: '',
  },
  rules: {
    type: [String, Object],
    default: () => '',
  },
  /**
   * Pour un formulaire avec plusieurs éléments qui ont le même nom et qu'il faut gérer la validation API sur le nom
   * P. ex. EmployeeTimetableWeekday ou CustomerCloseBooking
   */
  namePrefix: {
    type: String,
    default: '',
  },
  backgroundColor: {
    type: String,
    default: 'bg-white',
  },
  type: {
    type: String,
    default: 'text',
  },
  inputMode: {
    type: String,
    default: undefined,
  },
  textAlignRight: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String as PropType<TranslateResult | string>,
    default: '',
  },
  helpText: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  prependText: {
    // Valeur dans un input-prepend
    type: String,
    default: '',
  },
  prependIcon: {
    // Valeur dans un input-prepend
    type: String,
    default: '',
  },
  prependTextPadding: {
    type: Number,
    default: 14,
  },
  appendText: {
    type: String,
    default: '',
  },
  appendTextPadding: {
    type: Number,
    default: 12,
  },
  resetIcon: {
    // Valeur dans un input-prepend
    type: Boolean,
    default: false,
  },
  borderColor: {
    type: String,
    default: '',
  },
  mask: {
    type: Array,
    default: null,
    required: false,
  },
  invisible: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String,
    default: '',
  },
  optionalInputClass: {
    type: String,
    default: '',
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
});

const nameRef = toRef(props, 'name');
const rulesRef = toRef(props, 'rules');
const { apiErrorMessage } = useFormApiError(
  toRef(props, 'name'),
  toRef(props, 'apiErrors'),
);
const {
  value,
  meta,
  errors: veeErrors,
  handleBlur,
  handleChange,
} = useField(nameRef, rulesRef);

const input = ref<HTMLInputElement>();

onMounted(() => {
  if (props.autofocus) {
    input.value?.focus();
  }
});

const { shouldDisplayErrors } = useFormError(meta);
const { isOptional } = useFormHelper(toRef(props, 'rules'));
const revealed = ref(false);

const inputBorderClass = computed(() => {
  let inputBorderClass = '';
  if (props.invisible) {
    inputBorderClass +=
      'border-x-0 border-t-0 border-b border-b-gray-300 hover:border-b-gray-900 focus:border-x-0 focus:border-t-0 focus:border-b-2 focus:border-b-primary-700 focus:ring-0 ';
  } else {
    inputBorderClass += 'border focus:border-2 ';
    if (shouldDisplayErrors.value || apiErrorMessage.value) {
      inputBorderClass +=
        'border-red-500 focus:border-red-500 caret-red-500 focus:ring-0 ';
    } else {
      if (props.borderColor) {
        inputBorderClass +=
          ' border-' + props.borderColor + ' focus:border-' + props.borderColor;
      } else {
        inputBorderClass +=
          'border-gray-500 hover:border-gray-900 focus:border-primary-800 caret-primary-800 focus:ring-0 ';
      }
    }
  }

  return inputBorderClass;
});

const prependIconClass = 'text-gray-700 far ' + 'fa-' + props.prependIcon;
</script>
